import React, { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import config from "../config";

export default function DraftResultsSummary({
  leagueId: leagueIdProp,
  teamCount = 10,
  draftType = "SNAKE",
  salaryCap = 200,
  onBack,
}) {
  const { leagueId: leagueIdParam } = useParams();
  const leagueId = leagueIdProp ?? (leagueIdParam ? parseInt(leagueIdParam, 10) : null);

  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      if (!leagueId) {
        setError("League not found");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const playersResponse = await fetch(`${config.API_URL}/players`, {
          credentials: "include",
        });
        if (!playersResponse.ok) {
          throw new Error("Failed to fetch players data");
        }
        const playersData = await playersResponse.json();

        const enrichRoster = (roster) =>
          (roster || []).map((player) => {
            const playerData = playersData.find(
              (p) =>
                p.player_name === player.player_name ||
                String(p.player_id) === String(player.player_id)
            );
            return {
              player_id: String(player.player_id ?? player.id ?? ""),
              player_name: player.player_name,
              position: player.position,
              avg_fantasy_points: playerData?.avg_fantasy_points || 0,
              salary: player.salary ?? player.salaryCapPrice ?? 0,
            };
          });

        // Prefer draft-state (pick order, only teams that drafted).
        const stateResponse = await fetch(
          `${config.API_URL}/leagues/${leagueId}/draft-state`,
          { credentials: "include" }
        );
        if (stateResponse.ok) {
          const draftState = await stateResponse.json();
          const fromDraft = (draftState.teams || [])
            .map((team) => ({
              name: team.name || `Team ${(team.team_index ?? 0) + 1}`,
              roster: enrichRoster(team.roster),
            }))
            .filter((team) => team.roster.length > 0);

          if (fromDraft.length > 0) {
            setTeams(fromDraft);
            return;
          }
        }

        const teamsResponse = await fetch(
          `${config.API_URL}/leagues/${leagueId}/teams`,
          { credentials: "include" }
        );
        if (!teamsResponse.ok) {
          throw new Error("Failed to fetch team rosters");
        }

        const teamsData = await teamsResponse.json();
        const processedTeams = (teamsData.teams || [])
          .map((team) => ({
            name: team.name,
            roster: enrichRoster(team.roster),
          }))
          .filter((team) => team.roster.length > 0);

        setTeams(processedTeams);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load draft results");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [leagueId, location.key]);

  if (loading) {
    return (
      <div className="ffb-bg">
        <header className="ffb-header">
          <h1 className="ffb-header-title">🏈 Draft Results 🏈</h1>
        </header>
        <div style={{ padding: 32, textAlign: "center" }}>
          <div>Loading draft results...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ffb-bg">
        <header className="ffb-header">
          <h1 className="ffb-header-title">🏈 Draft Results 🏈</h1>
        </header>
        <div style={{ padding: 32, textAlign: "center" }}>
          <div style={{ color: "red" }}>{error}</div>
          <button className="ffb-btn" onClick={onBack} style={{ marginTop: 16 }}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  if (teams.length === 0) {
    return (
      <div className="ffb-bg">
        <header className="ffb-header">
          <h1 className="ffb-header-title">🏈 Draft Results 🏈</h1>
        </header>
        <div style={{ padding: 32, textAlign: "center" }}>
          <p>No draft results found for this league yet.</p>
          <button className="ffb-btn" onClick={onBack} style={{ marginTop: 16 }}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="ffb-bg">
      <header className="ffb-header">
        <h1 className="ffb-header-title">🏈 Draft Results 🏈</h1>
      </header>
      <div style={{ padding: 32, minHeight: 400, maxWidth: 900, margin: "0 auto" }}>
        <div style={{ marginBottom: 24, display: "flex", gap: 12 }}>
          <button className="ffb-btn" onClick={onBack}>
            Back to Home
          </button>
          <button
            onClick={() => {
              const text = teams
                .map((team) => {
                  return (
                    `${team.name}:\n` +
                    team.roster
                      .map((p, i) => {
                        const avgText =
                          p.avg_fantasy_points > 0
                            ? ` - Avg: ${p.avg_fantasy_points.toFixed(2)}`
                            : "";
                        const salaryText =
                          draftType === "SALARY_CAP" && p.salary
                            ? ` - $${p.salary}`
                            : "";
                        return `  ${i + 1}. ${p.player_name} (${p.position})${avgText}${salaryText}`;
                      })
                      .join("\n")
                  );
                })
                .join("\n\n");
              navigator.clipboard.writeText(text);
              alert("Draft results copied to clipboard!");
            }}
            className="ffb-btn"
          >
            Copy Results to Clipboard
          </button>
        </div>
        <h2 style={{ marginBottom: 24 }}>Draft Results</h2>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 16,
          }}
        >
          {teams.map((team) => (
            <div
              key={team.name}
              style={{
                flex: 1,
                minWidth: 260,
                background: "#f8fafc",
                borderRadius: 8,
                boxShadow: "0 2px 8px #e0e7ff",
                padding: 16,
              }}
            >
              <h3 style={{ color: "#2563eb", marginTop: 0 }}>{team.name}</h3>

              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {team.roster.map((p, i) => (
                  <li
                    key={`${team.name}-${p.player_id}-${i}`}
                    style={{ padding: "6px 0", borderBottom: "1px solid #e5e7eb" }}
                  >
                    <strong>Pick {i + 1}:</strong> {p.player_name}{" "}
                    <span style={{ color: "#64748b" }}>({p.position})</span>
                    {p.avg_fantasy_points > 0 && (
                      <span
                        style={{ color: "#0ea5e9", marginLeft: 8, fontSize: 13 }}
                      >
                        Avg: {p.avg_fantasy_points.toFixed(2)}
                      </span>
                    )}
                    {draftType === "SALARY_CAP" && p.salary > 0 && (
                      <span
                        style={{ color: "#059669", marginLeft: 8, fontSize: 13 }}
                      >
                        ${p.salary}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <footer
        style={{
          textAlign: "center",
          color: "#94a3b8",
          fontSize: "0.98em",
          marginTop: 40,
          padding: 20,
        }}
      />
    </div>
  );
}
